/**
 * Sugerencia de progresión (Req. 26.1, 26.2; design.md, 8.14).
 *
 * La app sugiere; la persona decide. Si la vez pasada todas las series de
 * trabajo llegaron al tope del rango de repeticiones con la misma carga,
 * propone subir el incremento del ejercicio. Nunca cambia el valor
 * prellenado por su cuenta (Req. 26.2): la sugerencia es un texto al lado, y
 * subirla es un toque de la persona.
 *
 * Se mira solo la última sesión, que es la que compara el entrenamiento en
 * curso ("La vez pasada"). Las series de calentamiento y las de dropset no
 * cuentan: no dicen si el rango se completó.
 *
 * Módulo puro: sin React y sin Dexie.
 */

import type { Exercise, WorkoutSet } from '@/db/types';
import { formatearNumero, redondear } from './numbers';
import { aKg, desdeKg, type UnidadCarga, type UnidadPeso } from './units';

/** El incremento cuando la carga se anota en lb (Req. 26.1). */
export const INCREMENTO_LB = 5;
/** El de kg sale de los ajustes (`incrementoPredeterminadoKg`), 2.5 por omisión. */
export const INCREMENTO_KG = 2.5;

export type SerieDeProgresion = Pick<WorkoutSet, 'tipoSerie' | 'pesoKg' | 'pesoCapturado' | 'reps' | 'hecha'> &
  Partial<Pick<WorkoutSet, 'repsIzq' | 'repsDer'>>;

export type Sugerencia =
  | {
      tipo: 'subir_carga';
      /** La carga de la vez pasada y la propuesta, en la unidad de hoy. */
      de: number;
      a: number;
      incremento: number;
      unidad: UnidadPeso;
      /** La propuesta en kg, para guardarla igual que cualquier carga. */
      nuevoKg: number;
      texto: string;
    }
  | { tipo: 'subir_placa'; de: number; a: number; texto: string }
  | { tipo: 'subir_rango'; repsMax: number; texto: string }
  | { tipo: 'mantener'; motivo: 'faltan_reps' | 'cargas_distintas'; texto: string }
  | { tipo: 'ninguna' };

/**
 * Las repeticiones que deciden si se completó el rango. En los ejercicios por
 * lado manda el lado que menos hizo: el rango se completa cuando los DOS
 * lados llegan, no cuando llega el bueno.
 */
export function repsParaProgresar(s: SerieDeProgresion): number {
  const izq = s.repsIzq ?? null;
  const der = s.repsDer ?? null;
  if (izq !== null || der !== null) return Math.min(izq ?? 0, der ?? 0);
  return s.reps ?? 0;
}

/** Las series que dicen si el rango se completó: hechas, normales o al fallo. */
export function seriesDeTrabajo(series: readonly SerieDeProgresion[]): SerieDeProgresion[] {
  return series.filter((s) => s.hecha && (s.tipoSerie === 'normal' || s.tipoSerie === 'fallo'));
}

const CASI = 1e-9;
const numero = (v: number) => formatearNumero(v, { decimales: 2, fijos: false });

const TEXTO_CARGAS_DISTINTAS =
  'Llegaste al tope del rango, pero con cargas distintas en cada serie: iguálalas antes de subir.';

/**
 * El incremento de este ejercicio, en kg: el suyo si lo tiene, o 5 lb cuando
 * la carga se anota en lb y el predeterminado en kg (Req. 26.1).
 */
export function incrementoEnKg(
  ejercicio: Pick<Exercise, 'incrementoKg'> | undefined,
  unidad: UnidadCarga,
  predeterminadoKg: number = INCREMENTO_KG,
): number {
  const suyo = ejercicio?.incrementoKg ?? null;
  if (suyo !== null && Number.isFinite(suyo) && suyo > 0) return suyo;
  if (unidad === 'lb') return aKg(INCREMENTO_LB, 'lb');
  return Number.isFinite(predeterminadoKg) && predeterminadoKg > 0 ? predeterminadoKg : INCREMENTO_KG;
}

export interface DatosDeProgresion {
  /** Las series hechas de la última sesión del ejercicio. */
  anteriores: readonly SerieDeProgresion[];
  /** El tope del rango de hoy; null en tiempo y distancia, y entonces no se sugiere nada. */
  repsMax: number | null;
  /** La unidad con que se anota la carga hoy. */
  unidad: UnidadCarga;
  ejercicio: Pick<Exercise, 'incrementoKg'> | undefined;
  incrementoPredeterminadoKg?: number;
}

/**
 * Qué sugerir para hoy, con lo que se hizo la vez pasada (Req. 26.1).
 *
 * - Todas las series de trabajo en el tope del rango y con la misma carga:
 *   subir el incremento.
 * - Con la carga en placas: la placa que sigue, porque un número de placa no
 *   es un peso y no se le puede sumar 2.5.
 * - Sin carga (peso corporal o 0 en casa): subir el rango o la variante; no
 *   tiene sentido proponer kilos donde no se usan.
 * - Si no llegó al tope, el mismo peso y una repetición más.
 */
export function sugerenciaDeProgresion(datos: DatosDeProgresion): Sugerencia {
  const { repsMax, unidad } = datos;
  const trabajo = seriesDeTrabajo(datos.anteriores);
  if (trabajo.length === 0 || repsMax === null || !(repsMax > 0)) return { tipo: 'ninguna' };

  const llegaronTodas = trabajo.every((s) => repsParaProgresar(s) >= repsMax);
  if (!llegaronTodas) {
    return {
      tipo: 'mantener',
      motivo: 'faltan_reps',
      texto: `La vez pasada no llegaste a ${repsMax} en todas las series: mismo peso y busca una repetición más.`,
    };
  }

  // Con la carga en placas no hay kilos que sumar: se propone la siguiente
  const placas = trabajo.map((s) => (s.pesoCapturado?.unidad === 'placa' ? s.pesoCapturado.valor : null));
  if (placas.every((p) => p !== null)) {
    const primera = placas[0] as number;
    if (!placas.every((p) => Math.abs((p as number) - primera) < CASI)) {
      return { tipo: 'mantener', motivo: 'cargas_distintas', texto: TEXTO_CARGAS_DISTINTAS };
    }
    const siguiente = primera + 1;
    return {
      tipo: 'subir_placa',
      de: primera,
      a: siguiente,
      texto: `Llegaste a ${repsMax} en todas con la placa ${numero(primera)}: puedes probar la ${numero(siguiente)}.`,
    };
  }

  const pesos = trabajo.map((s) => s.pesoKg ?? null);
  const sinCarga = pesos.every((p) => p === null || p === 0);
  if (sinCarga) {
    return {
      tipo: 'subir_rango',
      repsMax,
      texto: `Llegaste a ${repsMax} en todas las series sin carga: si ya se siente fácil, sube el rango de tu rutina o haz una variante más difícil.`,
    };
  }
  const primero = pesos[0] ?? null;
  if (primero === null || primero === 0 || !pesos.every((p) => p !== null && Math.abs(p - primero) < CASI)) {
    return { tipo: 'mantener', motivo: 'cargas_distintas', texto: TEXTO_CARGAS_DISTINTAS };
  }

  // La unidad de hoy puede no ser la de la vez pasada: se convierte una vez
  const enUnidad: UnidadPeso = unidad === 'placa' ? 'kg' : unidad;
  const incrementoKg = incrementoEnKg(datos.ejercicio, unidad, datos.incrementoPredeterminadoKg);
  const nuevoKg = primero + incrementoKg;
  const de = redondear(desdeKg(primero, enUnidad), 2);
  const a = redondear(desdeKg(nuevoKg, enUnidad), 2);
  const incremento = redondear(desdeKg(incrementoKg, enUnidad), 2);
  return {
    tipo: 'subir_carga',
    de,
    a,
    incremento,
    unidad: enUnidad,
    nuevoKg,
    texto: `Llegaste a ${repsMax} en todas con ${numero(de)} ${enUnidad}: puedes probar ${numero(a)} ${enUnidad} (+${numero(
      incremento,
    )}).`,
  };
}
